import isEqual from 'lodash/isEqual';
import { storeToRefs } from 'pinia';
import { computed, nextTick, ref, watch } from 'vue';
import { deepCompareComputed } from '@/composable/deepCompareComputed';
import { useState } from '@/composable/useState';
import BusinessLogicHelper from '@/core/lib/businessLogicHelper';
import { deepFreeze } from '@/core/lib/utils';
import { formatMpRecommendationEvent } from '@/core/oddsApi/helpers/formatEvent';
import { EnumOddsCategoryType, useMpRecommendationEventQuery, useMpRecommendationEventSubscription } from '@/core/oddsApi/oddsApiType';
import type { IMpRecommendationEvent } from '@/interface/IEvent';
import { useOddsApiStore } from '@/store/oddsApiStore';

export function useMpRecommendationEvent(
    eventId: number,
    isActive = ref(true),
) {
    const variables = computed(() => ({
        query: {
            id: eventId,
            oddsCategory: EnumOddsCategoryType.MixParlay,
        },
    }));

    const [isEnabled, setIsEnabled] = useState(true);
    const refetch = async () => {
        if (isEnabled.value) {
            setIsEnabled(false);
            nextTick(() => setIsEnabled(true));
        }
    };

    const { loaded, loading, onResult, error } = useMpRecommendationEventQuery(
        variables,
        deepCompareComputed(() => ({
            enabled: isActive.value && isEnabled.value,
        })),
    );

    const { onSubscription } = useMpRecommendationEventSubscription(
        variables,
        deepCompareComputed(() => ({
            enabled: isActive.value && isEnabled.value,
        })),
    );

    const event = ref<IMpRecommendationEvent | null>(null);
    const { customerSettings, additionalSettings } = storeToRefs(useOddsApiStore());

    onResult((result) => {
        if (result) {
            if (BusinessLogicHelper.isEventAllowed(customerSettings.value, result, additionalSettings.value)) {
                const newEvent = deepFreeze(formatMpRecommendationEvent(result));
                if (!isEqual(event.value, newEvent)) {
                    event.value = newEvent;
                }
            } else {
                event.value = null;
            }
        }
    });

    onSubscription((subscription) => {
        if (subscription && BusinessLogicHelper.isEventAllowed(customerSettings.value, subscription, additionalSettings.value)) {
            const newEvent = deepFreeze(formatMpRecommendationEvent(subscription));

            if (!isEqual(event.value, newEvent)) {
                event.value = newEvent;
            }
        } else {
            event.value = null;
        }
    });

    watch(variables, () => {
        loaded.value = false;
        event.value = null;
    });

    return {
        loaded,
        loading,
        refetch,
        event,
        error,
    };
}
